import { useState, useContext } from 'react';
import { useSelector } from 'react-redux';

// mui
import { Box, Card, CardActions, CardContent, CardHeader, makeStyles, Typography } from '@material-ui/core';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import LaunchIcon from '@material-ui/icons/Launch';

// app
import { Button, Chip, Link } from 'components';

import * as constants from 'consts';
import { selectUser } from 'stores';
import * as utils from 'utils';

import styles from './QuoteIssues.styles';

const IssueCard = ({
  issue,
  canCurrentUserDismissIssues,
  insuredSanctionsCheckResult,
  reInsuredSanctionsCheckResult,
  handleUpdateIssue,
  isWaiting,
}) => {
  const [clicked, setClicked] = useState(false);
  const classes = makeStyles(styles, { name: 'QuoteIssues' })();
  const issueStatus = issue?.issueStatus;
  const user = useSelector(selectUser);
  const isBroker = utils.user.isBroker(user);

  const hasInsuredSanctionsCheckResult = insuredSanctionsCheckResult?.id === issue?.entityId || false;
  const hasReInsuredSanctionsCheckResult = reInsuredSanctionsCheckResult?.id === issue?.entityId || false;
  const chipNestedClasses = { root: classes.issueTypeChip };

  const handleClickDismiss = () => {
    setClicked(true);
    handleUpdateIssue(issue.id, constants.RISK_ISSUE_STATUS_PASSED);
  };

  return (
    <Box data-testid="risk-data-issue">
      {hasInsuredSanctionsCheckResult ? (
        <Box mb={2} data-testid="insured-sanctions">
          <Chip label={utils.string.t('app.insured')} nestedClasses={chipNestedClasses} />
          <Typography className={classes.sectionMessage}>{insuredSanctionsCheckResult?.message}</Typography>
          {insuredSanctionsCheckResult?.url && isBroker && (
            <Link
              target="_blank"
              rel="noopener"
              nestedClasses={{
                link: classes.linkRoot,
                icon: classes.linkIcon,
              }}
              icon={LaunchIcon}
              iconPosition="right"
              href={insuredSanctionsCheckResult.url}
              color="primary"
              text={utils.string.t('risks.issueResolveLink')}
            />
          )}
        </Box>
      ) : null}
      {hasReInsuredSanctionsCheckResult ? (
        <Box mb={2} data-testid="reinsured-sanctions">
          <Chip label={utils.string.t('app.reInsured')} nestedClasses={chipNestedClasses} />
          <Typography className={classes.sectionMessage}>{reInsuredSanctionsCheckResult?.message}</Typography>
          {reInsuredSanctionsCheckResult?.url && isBroker && (
            <Link
              target="_blank"
              rel="noopener"
              nestedClasses={{
                link: classes.linkRoot,
                icon: classes.linkIcon,
              }}
              icon={LaunchIcon}
              iconPosition="right"
              href={reInsuredSanctionsCheckResult.url}
              color="primary"
              text={utils.string.t('risks.issueResolveLink')}
            />
          )}
        </Box>
      ) : null}

      {issue?.messages?.map((message) => {
        const createdAt = utils.string.t('format.date', {
          value: { date: message.createdAt, format: 'lll' },
        });

        return (
          <Box mb={2} key={message.createdAt}>
            <Card classes={{ root: classes.card }}>
              {(message?.userName || message?.createdAt) && (
                <CardHeader
                  className={classes.cardHeader}
                  title={message.userName}
                  subheader={<span title={` ${createdAt}`}>{createdAt}</span>}
                />
              )}
              <CardContent classes={{ root: classes.cardContent }}>
                <Typography variant="body2" className={classes.message}>
                  {message.message}
                </Typography>
              </CardContent>

              {constants.RISK_ISSUE_SANCTIONS.includes(issue.issueType) ? null : (
                <>
                  {canCurrentUserDismissIssues && issueStatus === constants.RISK_ISSUE_WAITING ? (
                    <CardActions disableSpacing>
                      <Box mb={0.5} mt={2} display="flex" flex={1} justifyContent="right">
                        <Button
                          icon={HighlightOffIcon}
                          size="xsmall"
                          color="primary"
                          text={utils.string.t('risks.dismissIssue')}
                          onClick={handleClickDismiss}
                          disabled={clicked || isWaiting}
                        />
                      </Box>
                    </CardActions>
                  ) : null}
                </>
              )}
            </Card>
          </Box>
        );
      })}
    </Box>
  );
};

export default IssueCard;
